import { useHost } from "vue";
import { requestConfirmation } from "#ce/components/SpConfirm/request-confirmation";
import { t, whenComponentsI18nReady } from "#ce/shared/components-i18n";
import { createActionFormApi } from "#ce/components/SpActionForm/api/action-form";

export const ActionFormStatus = Object.freeze({
  IDLE: "idle",
  CONFIRMING: "confirming",
  PENDING: "pending",
});

export const ActionFormEvent = Object.freeze({
  SEND: "action-send",
  SUCCESS: "action-success",
  ERROR: "action-error",
  COMPLETE: "action-complete",
});

const SUBMIT_ITEM = 'sp-button[type="submit"]';
const PENDING_ATTRIBUTES = ["loading", "disabled"];

// An attribute that is absent or "false" leaves the `sp-button` state off.
function isOff(item, name) {
  const value = item.getAttribute(name);

  return value === null || value === "false";
}

// Turns `item` loading and disabled and marks `form` busy. Returns a function that puts back the
// form's own `aria-busy` and the item attributes this call turned on.
function markPending(form, item) {
  const previous = PENDING_ATTRIBUTES.filter((name) => item && isOff(item, name)).map((name) => [
    name,
    item.getAttribute(name),
  ]);
  const previousBusy = form.getAttribute("aria-busy");

  previous.forEach(([name]) => item.setAttribute(name, ""));
  form.setAttribute("aria-busy", "true");

  return function restore() {
    previous.forEach(([name, value]) => (value === null ? item.removeAttribute(name) : item.setAttribute(name, value)));

    if (previousBusy === null) {
      form.removeAttribute("aria-busy");
    } else {
      form.setAttribute("aria-busy", previousBusy);
    }
  };
}

/**
 * Sends an `sp-action-form`'s slotted form: asks `props.confirm` first when it is not blank, marks
 * the pressed submit `sp-button` pending (the form's first one when none was pressed), and reports
 * the request through bubbling `action-*` events whose `detail` is an object. A submission while
 * one is still confirming or pending is dropped.
 *
 * @param {Object} props - The element's props.
 * @returns {{ handleClick: (event: MouseEvent) => void, handleSubmit: (event: SubmitEvent) => void }}
 *   `handleClick` remembers the submit item a click lands on, for that click only, because a
 *   submit `sp-button` reaches the form with no `submitter`. `handleSubmit` cancels the native submission and sends the form, unless another listener has
 *   already cancelled it.
 */
export function useActionForm(props) {
  const api = createActionFormApi();
  const host = useHost();
  let status = ActionFormStatus.IDLE;
  let pressedItem = null;

  function dispatch(type, detail) {
    host.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: false }));
  }

  function hasConfirmation() {
    return typeof props.confirm === "string" && props.confirm.trim() !== "";
  }

  // Labels are read once per question, so the translations are awaited first.
  async function askConfirmation() {
    await whenComponentsI18nReady;

    return requestConfirmation({
      title: t("action_form_confirm_title"),
      content: props.confirm,
      confirmLabel: t("action_form_confirm"),
      cancelLabel: t("action_form_cancel"),
    });
  }

  async function send(form, item) {
    if (status === ActionFormStatus.CONFIRMING && !(await askConfirmation())) {
      status = ActionFormStatus.IDLE;
      return;
    }

    status = ActionFormStatus.PENDING;

    const restore = markPending(form, item);
    let outcome;

    dispatch(ActionFormEvent.SEND, { form });

    try {
      outcome = [ActionFormEvent.SUCCESS, { form, data: await api.submit(form) }];
    } catch (error) {
      outcome = [ActionFormEvent.ERROR, { form, error }];
    }

    restore();
    status = ActionFormStatus.IDLE;
    dispatch(...outcome);
    dispatch(ActionFormEvent.COMPLETE, { form });
  }

  function forgetPressedItem() {
    pressedItem = null;
  }

  // A submit item submits inside its own click, so the remembered item is dropped in a task
  // after it. Microtasks run between a click's listeners, before the item's own one.
  function handleClick(event) {
    const item = event.target.closest?.(SUBMIT_ITEM);

    if (item) {
      pressedItem = item;
      setTimeout(forgetPressedItem);
    }
  }

  function handleSubmit(event) {
    if (event.defaultPrevented) {
      return;
    }

    const form = event.target;
    const item = event.submitter ?? pressedItem ?? form.querySelector(SUBMIT_ITEM);

    event.preventDefault();
    pressedItem = null;

    if (status !== ActionFormStatus.IDLE) {
      return;
    }

    status = hasConfirmation() ? ActionFormStatus.CONFIRMING : ActionFormStatus.PENDING;
    void send(form, item);
  }

  return { handleClick, handleSubmit };
}
